import { QRCodeSVG } from "qrcode.react";
import { money, fmtDate, fmtTime, addMinutes, METHOD_LABEL, fmtDateTime } from "@/lib/format";
import { Crest, ThermalBrand } from "@/components/Brand";

export const verifyUrl = (t) => `${window.location.origin}/verify/${t}`;
const mask = (p) => (p ? `${p.slice(0, 3)}****${p.slice(-3)}` : "");
const payLine = (b) => (b.payment_status === "paid" ? `PAID - ${METHOD_LABEL[b.payment_method]}` : b.payment_status === "refunded" ? "REFUNDED" : "UNPAID - pay at station");

function A4Ticket({ b, p }) {
  const t = b.trip;
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border-2 border-slate-200 bg-white sm:flex-row" style={{ breakInside: "avoid", marginBottom: 14 }} data-testid={`ticket-card-${p.ticket_no}`}>
      <div className="flex-1 p-5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2.5">
            <Crest className="h-11 w-11" />
            <span className="leading-tight"><span className="block font-display text-base font-extrabold text-[#182678]">GPRTU Intercity</span><span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">Developed by Nacasky Co. Ltd</span></span>
          </span>
          <span className="flex flex-wrap justify-end gap-1.5">
            {b.insurance && <span className="chip border border-emerald-200 bg-emerald-50 text-emerald-700" data-testid={`ticket-insured-${p.ticket_no}`}>Insured</span>}
            <span className={`chip border ${b.payment_status === "paid" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>{payLine(b)}</span>
          </span>
        </div>
        <div className="mt-4 flex items-end gap-4">
          <div><div className="label-xs">From</div><div className="font-display text-2xl font-extrabold">{t.origin_city}</div><div className="text-xs text-slate-500">{t.origin}</div></div>
          <div className="mb-2 flex-1 border-t-2 border-dashed border-blue-200" />
          <div className="text-right"><div className="label-xs">To</div><div className="font-display text-2xl font-extrabold">{t.destination_city}</div><div className="text-xs text-slate-500">{t.destination}</div></div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <Info k="Date" v={fmtDate(t.departure, { weekday: "short", day: "numeric", month: "short", year: "numeric" })} />
          <Info k="Departs" v={fmtTime(t.departure)} />
          <Info k="Arrives (est.)" v={addMinutes(t.departure, t.duration_min)} />
          <Info k="Bus" v={`${t.bus_number}`} sub={t.bus_type} />
          <Info k="Passenger" v={p.name} />
          <Info k="Gender" v={p.gender ? p.gender[0].toUpperCase() + p.gender.slice(1) : "-"} />
          <Info k="Phone" v={mask(p.phone)} />
          <Info k="Fare" v={money(b.fare)} sub={b.insurance ? `+ ${money(b.insurance_fee)} insurance` : null} />
          {b.online_fee > 0 && <Info k="Service fee" v={money(b.online_fee)} sub="Online booking" />}
          <Info k="Booking" v={b.code} mono />
        </div>
        <StopsLine t={t} />
      </div>
      <div className="flex items-center gap-4 border-t-2 border-dashed border-slate-200 bg-blue-50/60 p-5 sm:w-56 sm:flex-col sm:justify-center sm:border-l-2 sm:border-t-0">
        <div className="text-center">
          <div className="label-xs">Seat</div>
          <div className="font-display text-5xl font-extrabold text-[#0052FF]" data-testid={`ticket-seat-${p.ticket_no}`}>{p.seat}</div>
        </div>
        <div className="rounded-xl bg-white p-2"><QRCodeSVG value={verifyUrl(p.ticket_no)} size={104} level="H" imageSettings={{ src: "/brand/favicon-64.png", height: 22, width: 22, excavate: true }} /></div>
        <div className="font-mono text-xs font-bold tracking-widest text-blue-900">{p.ticket_no}</div>
      </div>
    </div>
  );
}

export function StopsLine({ t }) {
  const stops = t.stops || [];
  if (stops.length < 3) return null;
  return (
    <div className="mt-4 rounded-xl bg-slate-50 p-3" data-testid="ticket-stops">
      <div className="label-xs mb-1.5">Bus stops · get off at {t.destination_city}</div>
      <div className="flex flex-wrap items-center gap-1 text-[11px] font-semibold">
        {stops.map((s, i) => {
          const mine = i >= t.from_idx && i <= t.to_idx;
          return (
            <span key={s.station_id} className="flex items-center gap-1">
              {i > 0 && <span className="text-slate-300">›</span>}
              <span className={`rounded-md px-1.5 py-0.5 ${i === t.from_idx || i === t.to_idx ? "bg-[#182678] text-white" : mine ? "bg-blue-100 text-blue-800" : "text-slate-400"}`}>{s.city} {fmtTime(s.departure)}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

const Info = ({ k, v, sub, mono }) => (
  <div><div className="label-xs">{k}</div><div className={`font-semibold text-slate-900 ${mono ? "font-mono tracking-wider" : ""}`}>{v}</div>{sub && <div className="text-xs text-slate-500">{sub}</div>}</div>
);

function ThermalTicket({ b, p, last }) {
  const t = b.trip;
  return (
    <div className={`thermal ${last ? "" : "page-break"}`}>
      <ThermalBrand />
      <div className="center">{t.origin}</div>
      <hr />
      <div className="center big">{t.origin_city} &gt; {t.destination_city}</div>
      <div className="row"><span>Date</span><span>{fmtDate(t.departure)}</span></div>
      <div className="row"><span>Departure</span><span>{fmtTime(t.departure)}</span></div>
      <div className="row"><span>Bus</span><span>{t.bus_number}</span></div>
      <div className="row"><span>Type</span><span>{t.bus_type}</span></div>
      <hr />
      <div className="row big"><span>SEAT</span><span>{p.seat}</span></div>
      <div className="row"><span>Passenger</span><span>{p.name}</span></div>
      <div className="row"><span>Gender</span><span>{p.gender ? p.gender[0].toUpperCase() + p.gender.slice(1) : "-"}</span></div>
      <div className="row"><span>Phone</span><span>{mask(p.phone)}</span></div>
      <div className="row"><span>Fare</span><span>{money(b.fare)}</span></div>
      {b.insurance && <div className="row"><span>Insurance</span><span>{money(b.insurance_fee)}</span></div>}
      {b.online_fee > 0 && <div className="row"><span>Service fee</span><span>{money(b.online_fee)}</span></div>}
      <div className="row"><span>Status</span><span>{payLine(b)}</span></div>
      {b.teller_name && <div className="row"><span>Teller</span><span>{b.teller_name}</span></div>}
      {(t.stops || []).length > 2 && (
        <>
          <hr />
          <div className="center">BUS STOPS</div>
          {t.stops.map((s, i) => <div key={s.station_id} className="row"><span>{i === t.from_idx ? "> " : i === t.to_idx ? "* " : "  "}{s.city}</span><span>{fmtTime(s.departure)}</span></div>)}
          <div className="center">&gt; board here  * get off here</div>
        </>
      )}
      <hr />
      <div className="center" style={{ margin: "6px 0" }}><QRCodeSVG value={verifyUrl(p.ticket_no)} size={120} /></div>
      <div className="center big">{p.ticket_no}</div>
      <div className="center">Issued {fmtDateTime(b.paid_at || b.created_at)}</div>
      <div className="center">Scan QR to verify. Report 30 min before departure.</div>
      <hr />
    </div>
  );
}

export default function TicketView({ booking, format = "a4" }) {
  const list = booking.passengers;
  if (format === "thermal") return <>{list.map((p, i) => <ThermalTicket key={p.ticket_no} b={booking} p={p} last={i === list.length - 1} />)}</>;
  return <div className="a4-list">{list.map((p) => <A4Ticket key={p.ticket_no} b={booking} p={p} />)}</div>;
}
